import React, { useState, useRef } from 'react';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL, POST_DEFAULT_VALUES } from '../../utils/config';

// Full screen loader with progress
const ProgressLoader = ({ progress, current, total, status }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
          <FiUpload className="text-indigo-600" size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Uploading Default Values</h3>
        <p className="text-gray-600 text-sm">{status}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress: {current} / {total}</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="bg-gradient-to-r from-indigo-600 to-purple-600 h-4 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        Please wait while default values are being uploaded...
      </div>
    </div>
  </div>
);

const AddDefaultData = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState('');
  const [uploadResults, setUploadResults] = useState({ success: 0, failed: 0, errors: [] });
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('accessToken');
  
  // Get tenant name from clientInfo or localStorage
  const getTenantName = () => {
    try {
      const clientInfoStr = localStorage.getItem('clientInfo');
      if (clientInfoStr) {
        const clientInfo = JSON.parse(clientInfoStr);
        if (clientInfo.TenantName) {
          return clientInfo.TenantName;
        }
      }
    } catch (e) {
      console.error('Error parsing clientInfo:', e);
    }
    
    return localStorage.getItem('firm_name') || '';
  };

  // Helper function to convert Excel value to proper type
  const convertValue = (value) => {
    if (value === null || value === undefined || value === 'NULL' || value === '') {
      return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
    return value;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setParsing(true);
        setUploadResults({ success: 0, failed: 0, errors: [] });
        
        try {
          // Parse Excel file immediately
          const data = await parseExcelFile(selectedFile);
          if (data && data.length > 0) {
            setParsedData(data);
            toast.success(`Successfully read ${data.length} default values from file`, { autoClose: 2000 });
          } else {
            toast.error('Excel file is empty or invalid', { autoClose: 2000 });
            setFile(null);
            setParsedData([]);
          }
        } catch (error) {
          console.error('Error parsing file:', error);
          toast.error('Failed to parse Excel file: ' + (error.message || 'Unknown error'), { autoClose: 2000 });
          setFile(null);
          setParsedData([]);
        } finally {
          setParsing(false);
        }
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)', { autoClose: 2000 });
        setFile(null);
        setParsedData([]);
      }
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const uploadDefaultValue = async (rowData, index) => {
    try {
      // Map Excel columns to API fields
      // Excel: TestID, SI_No, TestName, Value, Section, IsDefault
      // API: TestID, TestName, Values, Section, IsDefault
      const testID = rowData.TestID || rowData['TestID'];
      const testName = convertValue(rowData.TestName || rowData['TestName']) || '';
      const values = convertValue(rowData.Value || rowData['Value']) || '';
      const section = convertValue(rowData.Section || rowData['Section']) || '';
      const isDefault = rowData.IsDefault || rowData['IsDefault'] || 0;

      if (!testID || !testName) {
        return {
          success: false,
          index,
          error: 'TestID and TestName are required'
        };
      }

      const response = await axios.post(`${BASE_URL}${POST_DEFAULT_VALUES}`, {}, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY || '',
          'Authorization': `Bearer ${token}`,
          'TestID': testID.toString(),
          'TestName': testName,
          'Values': values,
          'Section': section,
          'IsDefault': isDefault ? 1 : 0,
        },
      });

      if (response.status === 200) {
        return { success: true, index };
      } else {
        return { success: false, index, error: 'API returned non-200 status' };
      }
    } catch (error) {
      return {
        success: false,
        index,
        error: error.response?.data?.message || error.message || 'Unknown error'
      };
    }
  };

  const handleUpload = async () => {
    if (!file || parsedData.length === 0) {
      toast.error('Please select and parse an Excel file first', { autoClose: 2000 });
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    setUploading(true);
    setProgress(0);
    setCurrentIndex(0);
    setUploadResults({ success: 0, failed: 0, errors: [] });

    try {
      setTotalItems(parsedData.length);
      setStatus(`Uploading ${parsedData.length} default values...`);

      // Upload default values one by one with delay
      const results = { success: 0, failed: 0, errors: [] };

      for (let i = 0; i < parsedData.length; i++) {
        setCurrentIndex(i + 1);
        const testName = parsedData[i].TestName || parsedData[i]['TestName'] || 'Unknown';
        setStatus(`Uploading ${i + 1} of ${parsedData.length}: ${testName}`);

        const result = await uploadDefaultValue(parsedData[i], i);
        
        if (result.success) {
          results.success++;
        } else {
          results.failed++;
          results.errors.push({
            index: i + 1,
            testName: testName,
            error: result.error
          });
        }

        // Update progress
        const newProgress = Math.round(((i + 1) / parsedData.length) * 100);
        setProgress(newProgress);

        // Small delay between API calls (300ms)
        if (i < parsedData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setUploadResults(results);
      setStatus('Upload completed!');

      // Show results
      if (results.failed === 0) {
        toast.success(`Successfully uploaded all ${results.success} default values!`, { autoClose: 3000 });
      } else {
        toast.warning(`Uploaded ${results.success} default values. ${results.failed} failed.`, { autoClose: 3000 });
      }

      // Wait a bit before allowing user to continue
      await new Promise(resolve => setTimeout(resolve, 2000));
      setUploading(false);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to process Excel file: ' + (error.message || 'Unknown error'), { autoClose: 3000 });
      setUploading(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {uploading && (
        <ProgressLoader
          progress={progress}
          current={currentIndex}
          total={totalItems}
          status={status}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FiFile size={32} className="text-blue-600" />
          Add Default Data
        </h1>
        <p className="text-gray-500 mt-2">Upload Excel file to bulk add default values for tests</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="space-y-6">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
                id="file-upload-default"
              />
              <label
                htmlFor="file-upload-default"
                className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FiUpload size={48} className="text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {file ? file.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-400">
                  Excel files only (.xlsx, .xls)
                </p>
              </label>
            </div>
            {file && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FiCheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">{file.name}</span>
                {parsing && (
                  <span className="text-blue-600 text-sm ml-2">(Parsing...)</span>
                )}
                {!parsing && parsedData.length > 0 && (
                  <span className="text-green-600 text-sm ml-2">({parsedData.length} values found)</span>
                )}
                <button
                  onClick={() => {
                    setFile(null);
                    setParsedData([]);
                    setUploadResults({ success: 0, failed: 0, errors: [] });
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="ml-auto text-red-600 hover:text-red-700"
                  disabled={uploading || parsing}
                >
                  <FiXCircle size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Excel Format Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="text-blue-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Expected Excel Format</h3>
                <p className="text-sm text-blue-700 mb-2">
                  Your Excel file should contain the following columns:
                </p>
                <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                  <li><strong>Required:</strong> TestID, TestName, Value, Section</li>
                  <li><strong>Optional:</strong> SI_No, IsDefault (0 or 1)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Parsed Data Table */}
          {!parsing && parsedData.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Preview Data ({parsedData.length} values)
                </h3>
                <span className="text-sm text-gray-500">
                  Review the data before submitting
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TestID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SI_No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IsDefault</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.map((row, index) => {
                        const testID = row.TestID || row['TestID'] || '';
                        const siNo = row.SI_No || row['SI_No'] || '';
                        const testName = row.TestName || row['TestName'] || '';
                        const value = row.Value || row['Value'] || '';
                        const section = row.Section || row['Section'] || '';
                        const isDefault = row.IsDefault || row['IsDefault'] || 0;
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{testID}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{siNo || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{testName}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{value}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{section}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{isDefault}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {parsedData.length > 0 && (
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setFile(null);
                  setParsedData([]);
                  setUploadResults({ success: 0, failed: 0, errors: [] });
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                disabled={uploading || parsing}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all ${
                  (uploading || parsing) ? 'opacity-70 cursor-not-allowed' : ''
                }"
              >
                Clear
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading || parsing || parsedData.length === 0}
                className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg ${
                  (!file || uploading || parsing || parsedData.length === 0) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Uploading...' : `Submit ${parsedData.length} Values`}
              </button>
            </div>
          )}

          {/* Upload Results */}
          {!uploading && uploadResults.success + uploadResults.failed > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Upload Results</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{uploadResults.success}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{uploadResults.failed}</p>
                </div>
              </div>
              {uploadResults.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {uploadResults.errors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 p-2 bg-red-50 rounded">
                        Row {error.index}: {error.testName} - {error.error}
                      </div>
                    ))}
                    {uploadResults.errors.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        ... and {uploadResults.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDefaultData;
